import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { APP_PATHS, XEMEX_OID } from '@core/constants';
import {
  canUseInstallationFeature,
  canViewInstallations,
} from '@core/helpers/installation-access.helper';
import { explainedInPlace, toRefusal } from '@core/helpers/installation-refusal.helper';
import { installerOidFor } from '@core/helpers/installer-oid.helper';
import {
  DeviceModel,
  Installation,
  InstallationStatusEnum,
  MeterRole,
  PhaseCount,
  StartReadoutData,
  getUserFeaturePermissions,
  getUserRole,
} from '@core/models';
import { DeviceService, InstallationService } from '@core/services';
import { TextButtonComponent } from '@custom-components/general/button/text-button/text-button.component';
import { CardComponent } from '@custom-components/general/card';
import { DetailFieldComponent } from '@custom-components/general/detail-field/detail-field.component';
import { SkeletonBlockComponent } from '@custom-components/general/skeleton/skeleton-block/skeleton-block.component';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import {
  StatusBadgeType,
  StatusChipComponent,
} from '@custom-components/general/status-chip/status-chip.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthStore } from '@root/app/store/auth.store';
import {
  AttributeRead,
  DeviceAttributeReads,
  InstallationAttributesStore,
} from '@root/app/store/installation-attributes.store';
import { UserStore } from '@root/app/store/user.store';
import { Observable, finalize } from 'rxjs';
import {
  LinkDeviceDialogComponent,
  LinkDeviceDialogData,
  LinkDeviceResult,
} from '../../../locations/components/link-device-dialog/link-device-dialog.component';
import { DeviceReadoutDialogComponent } from '../../../devices/components/device-readout-dialog/device-readout-dialog.component';
import { METER_ROLE_EDITOR_ROLES } from '../../../devices/constants/meter-role-editor-roles.constant';
import { otherInstallations } from '../../device-attributes';
import { DeviceStatusKind, deviceStatusKind } from '../../device-status';
import { FedDeviceDetails } from '../../fed-devices';
import { LinkNotice, linkedNotice, refusedNotice, takeLinkNotice } from '../../link-notice';
import { linkRefusal } from '../../link-refusal';
import { LinkResult, linkResult } from '../../link-result';
import {
  MeterRoleDialogComponent,
  MeterRoleDialogData,
} from '../meter-role-dialog/meter-role-dialog.component';

const LABELS = 'installations.attributes';

export interface AttributeCell {
  loading: boolean;
  text: string;
  // Where the value comes from, or what holds it in place.
  note: string | null;
}

export interface DeviceRow {
  deviceId: number;
  ldn: string;
  typeName: string | null;
  status: { chip: StatusBadgeType; label: string } | null;
  // Null where the value is not shown: attributes are not read for this role, or the device does
  // not say (an unset meter role, a phase count still being detected).
  phaseCount: AttributeCell | null;
  meterRole: AttributeCell | null;
  // Set when the user may change the role, the device is one the route accepts and its role is known.
  canChangeMeterRole: boolean;
  feedsNoPoint: boolean;
  readout: boolean;
}

const STATUS_CHIPS: Record<DeviceStatusKind, StatusBadgeType> = {
  online: 'success',
  offline: 'error',
  never: 'inactive',
};

const LOADING_CELL: AttributeCell = { loading: true, text: '', note: null };

function isCounted(phaseCount: PhaseCount | null): phaseCount is '1' | '3' {
  return phaseCount === '1' || phaseCount === '3';
}

// Every device feeding the installation or a member of it, with the phase count and meter role read from the device
// where prd keeps them. Nothing is computed across devices: each value belongs to one meter.
@Component({
  selector: 'xemex-installation-devices-card',
  imports: [
    CardComponent,
    DetailFieldComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    RouterLink,
    SkeletonBlockComponent,
    SpinnerComponent,
    StatusChipComponent,
    TextButtonComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-devices-card.component.html',
  styleUrl: './installation-devices-card.component.scss',
})
export class InstallationDevicesCardComponent {
  installation = input.required<Installation>();
  devices = input.required<FedDeviceDetails[]>();
  // The device type and online status come from the data sources, which are read apart from the
  // attributes.
  detailsLoading = input<boolean>(false);
  // The attributes are read from the devices, so a role that may not see devices is not shown them.
  showAttributes = input<boolean>(false);
  // A saved meter role re-binds the installation's points, so the page reads them again.
  bindingsChanged = output<void>();
  // A device was linked, which can bind points and add a device at the location.
  deviceLinked = output<void>();
  // A link that bound nothing, so the page brings the profile card, which lists the points still
  // awaiting a channel, into view.
  profileRequested = output<void>();
  // A refused link found the installation changed or gone, so the page reads it again.
  installationChanged = output<void>();

  private readonly store = inject(InstallationAttributesStore);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);
  private readonly deviceService = inject(DeviceService);
  private readonly installationService = inject(InstallationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  protected readonly APP_PATHS = APP_PATHS;

  private readonly deviceIds = computed(() => this.devices().map(device => device.deviceId));

  // As on the device page, a readout is offered for a DLMS device, to a role that may read its
  // telemetry.
  private readonly mayReadOut = computed(() =>
    getUserFeaturePermissions(
      this.userStore.user(),
      this.authStore.permissions(),
      XEMEX_OID,
    ).includes('view_device_telemetry'),
  );

  // Each edit asks for the role or feature its route checks, which the device page's own editors
  // ask for too. Nothing is edited from a Retired installation, not even the devices it was fed by.
  private readonly mayEdit = computed(
    () =>
      this.showAttributes() &&
      this.installation().status === 'Active' &&
      canViewInstallations(this.userStore.user(), this.authStore.permissions()),
  );

  // The two ways to link are the two features: a Linked device is provisioned, a Registered one is
  // added to the installation, and the dialog offers only what the user may do.
  private readonly linkableStatuses = computed(() => {
    const user = this.userStore.user();
    const permissions = this.authStore.permissions();
    const statuses: InstallationStatusEnum[] = [];
    if (canUseInstallationFeature(user, permissions, 'provision_device')) {
      statuses.push(InstallationStatusEnum.Linked);
    }
    if (canUseInstallationFeature(user, permissions, 'edit_installation_bindings')) {
      statuses.push(InstallationStatusEnum.Registered);
    }
    return statuses;
  });

  // A hand-made installation has no feeding device yet, so the action does not depend on one.
  protected readonly mayLinkDevice = computed(
    () =>
      this.installation().status === 'Active' &&
      !!this.installation().location.partnerOid &&
      this.linkableStatuses().length > 0,
  );

  protected readonly linking = signal(false);
  // The device whose readout is being started; the dialog that follows it takes over from there.
  protected readonly readingOut = signal<number | null>(null);
  // Set when the add installation dialog opened this page after linking a device.
  protected readonly linkNotice = signal<LinkNotice | null>(takeLinkNotice(this.location));

  private readonly mayChangeMeterRole = computed(() => {
    const roleName = getUserRole(this.userStore.user())?.roleName;
    return this.mayEdit() && !!roleName && METER_ROLE_EDITOR_ROLES.includes(roleName);
  });

  readonly rows = computed<DeviceRow[]>(() => {
    const showAttributes = this.showAttributes();
    return this.devices().map(device => {
      const reads = this.readsOf(device.deviceId);
      const mayChange = this.mayChangeMeterRole() && this.isDlms(reads.device);
      const meterRole = showAttributes ? this.meterRoleCell(device.deviceId, mayChange) : null;
      return {
        deviceId: device.deviceId,
        ldn: device.ldn,
        typeName: device.typeName,
        status: this.deviceStatus(device.onlineStatus),
        phaseCount: showAttributes ? this.phaseCountCell(reads.phaseCount) : null,
        meterRole,
        canChangeMeterRole: mayChange && !!meterRole && !meterRole.loading,
        feedsNoPoint: !!device.feedsNoPoint,
        readout: this.mayReadOut() && device.protocol === 'DLMS',
      };
    });
  });

  constructor() {
    this.store.load(computed(() => (this.showAttributes() ? this.deviceIds() : [])));
    // The profile card is only there to scroll to once the page has rendered.
    afterNextRender(() => {
      if (this.linkNotice()?.offersChecklist) this.profileRequested.emit();
    });
  }

  // The profile card only shows which points await a choice; the channels are chosen on the Data
  // sources tab.
  chooseSources(): void {
    void this.router.navigate([
      '/',
      APP_PATHS.Installations,
      this.installation().installationOid,
      'data-sources',
    ]);
  }

  changeMeterRole(deviceId: number): void {
    const reads = this.readsOf(deviceId);
    const resolved = this.store.meterRoleOf(deviceId);
    if (resolved.state !== 'resolved') return;

    const ref = this.dialog.open<MeterRoleDialogComponent, MeterRoleDialogData, MeterRole>(
      MeterRoleDialogComponent,
      {
        panelClass: 'no-padding-dialog',
        width: '32rem',
        maxWidth: 'calc(100vw - 2rem)',
        data: {
          deviceId,
          ldn: this.devices().find(device => device.deviceId === deviceId)?.ldn ?? String(deviceId),
          current: resolved.role,
          origin: resolved.origin,
          otherInstallations: otherInstallations(
            reads.channelUsage,
            this.installation().installationOid,
          ),
        },
      },
    );
    const editor = ref.componentInstance;
    ref.afterClosed().subscribe(role => {
      if (role === undefined && !editor.stale()) return;
      this.store.reloadDevice(deviceId);
      if (role !== undefined) this.bindingsChanged.emit();
    });
  }

  // The device page's readout: the same request, and the same dialog following it.
  readOut(deviceId: number): void {
    const id = String(deviceId);
    this.readingOut.set(deviceId);
    this.deviceService
      .performReadout(id)
      .pipe(
        finalize(() => this.readingOut.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: result => this.openReadout({ polId: result.polId, deviceId: id, error: '' }),
        error: (error: unknown) =>
          this.openReadout({ polId: '', deviceId: id, error: JSON.stringify(error) }),
      });
  }

  linkDevice(): void {
    const { installationOid, location } = this.installation();
    if (!location.partnerOid) return;
    this.dialog
      .open<LinkDeviceDialogComponent, LinkDeviceDialogData, LinkDeviceResult>(
        LinkDeviceDialogComponent,
        {
          width: '48rem',
          maxWidth: 'calc(100vw - 2rem)',
          maxHeight: 'calc(100vh - 2rem)',
          data: {
            locationId: location.locationId,
            partnerOid: location.partnerOid,
            installationOid,
            provisioning: this.linkableStatuses(),
          },
        },
      )
      .afterClosed()
      .subscribe(result => {
        if (!result) return;
        if (result.provisioning === InstallationStatusEnum.Registered) {
          this.linkRegistered(result.deviceId);
        } else {
          this.provision(result.deviceId);
        }
      });
  }

  dismissLinkNotice(): void {
    this.linkNotice.set(null);
  }

  private provision(deviceId: string): void {
    const { installationOid, location } = this.installation();
    const installerOid = installerOidFor(this.userStore.user(), location.partnerOid);
    if (!installerOid) return;
    this.link(
      this.deviceService.provisionDevice(
        deviceId,
        location.locationId,
        installerOid,
        undefined,
        installationOid,
        explainedInPlace(),
      ),
      deviceId,
    );
  }

  private linkRegistered(deviceId: string): void {
    this.link(
      this.installationService.linkDevice(this.installation().installationOid, deviceId),
      deviceId,
    );
  }

  private link(request: Observable<unknown>, deviceId: string): void {
    this.linking.set(true);
    this.linkNotice.set(null);
    request
      .pipe(
        finalize(() => this.linking.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => this.linked(linkResult(response, deviceId)),
        error: (error: unknown) => {
          const refusal = linkRefusal(toRefusal(error));
          this.linkNotice.set(refusedNotice(refusal));
          if (refusal.stale) this.installationChanged.emit();
        },
      });
  }

  private linked(result: LinkResult | null): void {
    this.deviceLinked.emit();
    if (!result) return;
    const notice = linkedNotice(result);
    this.linkNotice.set(notice);
    // The notice grows this card and pushes the profile card down, so the page scrolls to it only
    // once the notice has rendered.
    if (notice.offersChecklist) {
      afterNextRender(() => this.profileRequested.emit(), { injector: this.injector });
    }
  }

  private openReadout(data: StartReadoutData): void {
    this.dialog.open<DeviceReadoutDialogComponent, StartReadoutData>(DeviceReadoutDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      data,
    });
  }

  // The route refuses a device that is not a DLMS meter, so the action is not offered for one.
  private isDlms(read: AttributeRead<DeviceModel>): boolean {
    return read.state === 'loaded' && read.value.protocol === 'DLMS';
  }

  private readsOf(deviceId: number): DeviceAttributeReads {
    return (
      this.store.devices()[deviceId] ?? {
        device: { state: 'loading' },
        phaseCount: { state: 'loading' },
        pvConfig: { state: 'loading' },
        channelUsage: { state: 'loading' },
      }
    );
  }

  private phaseCountCell(read: DeviceAttributeReads['phaseCount']): AttributeCell | null {
    if (read.state === 'loading') return LOADING_CELL;
    if (read.state !== 'loaded') return this.failureCell(read.state);
    const { phaseCount, phaseCountFixed } = read.value;
    if (!isCounted(phaseCount)) return null;
    const noteKey = this.phaseCountNoteKey(phaseCountFixed === true);
    return {
      loading: false,
      text: phaseCount,
      note: noteKey ? this.translate.instant(noteKey) : null,
    };
  }

  private phaseCountNoteKey(fixed: boolean): string | null {
    return fixed ? `${LABELS}.phaseCount.fixedByDeviceType` : null;
  }

  // A device with neither its own role nor a partner default shows none, unless the user may set
  // one: the change then opens on the role the device page falls back to.
  private meterRoleCell(deviceId: number, showFallback: boolean): AttributeCell | null {
    const resolved = this.store.meterRoleOf(deviceId);
    if (resolved.state === 'loading') return LOADING_CELL;
    if (resolved.state !== 'resolved') return this.failureCell(resolved.state);
    if (resolved.origin === 'fallback' && !showFallback) return null;
    return this.meterRole(resolved.role);
  }

  private meterRole(role: MeterRole): AttributeCell {
    return {
      loading: false,
      text: this.translate.instant(`meterRoles.${role}`),
      note: null,
    };
  }

  private failureCell(state: 'notShared' | 'failed'): AttributeCell {
    return {
      loading: false,
      text: this.translate.instant(`${LABELS}.${state}`),
      note: null,
    };
  }

  private deviceStatus(onlineStatus: string | null): DeviceRow['status'] {
    const kind = deviceStatusKind(onlineStatus);
    return kind
      ? {
          chip: STATUS_CHIPS[kind],
          label: this.translate.instant(`installations.dataSources.device.${kind}`),
        }
      : null;
  }
}
